import { useRef, useState, useMemo, useEffect } from 'react';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';

import { Box, Modal, ModalFooter, ModalHeader, AgGridTable, BodySmall } from '@parspec/pixel';

import WarningCell from './WarningCell';

import { getRowId } from './utils';
import { MainProductDataType } from 'src/features/BOM/pricing&LeadTime/mainProductTable';
import { FIXED_COLUMNS } from '../../constants';

interface ManageColumnsPropsT {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSnackbarData: React.Dispatch<React.SetStateAction<string>>;
    allColumnOptions: { field: string; label: string }[];
    onColumnSelection: (data: string[]) => void;
    selectedColumnsData: string[] | null;
    isSelectedColumnsMutationLoading?: boolean;
    isSelectedColumnsQueryDataFetching: boolean;
    tab?: boolean;
    tableData?: MainProductDataType[]
    updateNotificationCount?: (value: number) => void
}
const orderedFieldsSet = new Set(FIXED_COLUMNS);

const defaultColDef: ColDef = {
    flex: 1,
    resizable: true,
    suppressFillHandle: true,
    suppressMenu: true,
    sortable: false,
    unSortIcon: true,
    menuTabs: [],
    wrapHeaderText: true,
    suppressMovable: true,
    editable: false
};


export const ManageColumnsPopup = ({
    isOpen,
    setIsOpen,
    allColumnOptions,
    setSnackbarData,
    onColumnSelection,
    selectedColumnsData,
    isSelectedColumnsMutationLoading,
    isSelectedColumnsQueryDataFetching,
    tab,
    tableData,
}: ManageColumnsPropsT) => {
    const [searchText, setSearchText] = useState('');
    const [rowDataOptions, setRowDataOptions] = useState<{ field: string; label: string }[]>([]);
    const [isGridReady, setIsGridReady] = useState(false);

    const tableRef = useRef<AgGridReact | null>(null);

    useEffect(() => {
        if (isSelectedColumnsQueryDataFetching) {
            return;
        }
        if (selectedColumnsData) {
            const allColumnOptionsObj = allColumnOptions.reduce((acc, curr) => {
                acc[curr.field] = curr;
                return acc;
            }, {} as { [key: string]: { field: string; label: string } });
            const selectedColumnDataSet = new Set(selectedColumnsData);
            const resultData = selectedColumnsData
                .map((field) => allColumnOptionsObj[field])
                .filter((item) => item !== undefined)
                .concat(allColumnOptions.filter((item) => !selectedColumnDataSet.has(item.field)));

            const orderedFields = resultData.filter(item => orderedFieldsSet.has(item.field));
            const remainingFields = resultData.filter(item => !orderedFieldsSet.has(item.field));

            const sortedFields = [
                ...orderedFields.filter(item => item.field === 'category'),
                ...orderedFields.filter(item => item.field !== 'category')
            ];

            const finalOrderedFields = [...sortedFields, ...remainingFields];
            setRowDataOptions(finalOrderedFields);
        } else {
            setRowDataOptions(allColumnOptions);
        }
    }, [allColumnOptions, isSelectedColumnsQueryDataFetching, selectedColumnsData]);

    useEffect(() => {
        if (!isGridReady || !rowDataOptions.length) return;
        // In case selected array is empty or not present, select all as per prev logic
        if (!selectedColumnsData?.length) {
            tableRef.current?.api.forEachNode((node) => {
                node.setSelected(true);
            });
            return;
        }

        tableRef.current?.api.forEachNode((node) => {
            node.setSelected(selectedColumnsData.includes(node.data.field));
        });
    }, [isGridReady, selectedColumnsData, rowDataOptions])

    const onGridReady = () => {
        setIsGridReady(true);
    };

    const closeModalHandler = () => {
        setIsOpen(() => false);
    };

    const handleSearch = (searchString: string) => {
        setSearchText(searchString);
    };

    const columnDefs = useMemo<any>(() => {
        const columns = [
            { rowDrag: !tab, width: 10, maxWidth: 50, resizable: false, editable: false },
            {
                headerCheckboxSelection: true, showDisabledCheckboxes: true,
                checkboxSelection: (params: any) => {
                    return !orderedFieldsSet.has(params.data.field)
                },
                width: 50, maxWidth: 50, resizable: false
            },

            {
                field: 'label',
                minWidth: 100,
                headerName: 'Column Name',

            },
            { width: 10, maxWidth: 50, resizable: false, editable: false, cellRenderer: (params: CustomCellRendererProps) => WarningCell(params, tableData || []) },
        ];
        return columns;
    }, [tab, tableData]);

    const onAccept = () => {
        const rowData: any[] = [];
        tableRef.current?.api.forEachNode((node) => {
            if (node.isSelected()) {
                rowData.push(node.data);
            }
        });

        if (rowData.length === 0) {
            return setSnackbarData('Please select at least one column');
        }

        const finalRowData = [...rowData.map((el: { field: string; label: string }) => el.field)].filter((value: string, index: number, self: string[]) => index === self.findIndex((item: string) => item === value));
        onColumnSelection(finalRowData);
    };


    const handleCheckboxChange = () => {
        tableRef.current?.api.forEachNode((node) => {
            if (orderedFieldsSet?.has(node.data.field)) {
                node.setSelected(true);
            }
        });

    };
    return (
        <Box>
            <Modal
                open={isOpen}
                footer={<ModalFooter isLoading={isSelectedColumnsMutationLoading} onAccept={onAccept} onReject={closeModalHandler} />}
                header={<ModalHeader onClose={closeModalHandler} title={'Manage Columns'}></ModalHeader>}
                onClose={closeModalHandler}
            >
                <Box width={'400px'}>
                    <Box mb={6}>
                        <BodySmall color={'text.secondary'} limit={false}>
                            {tab ? 'Choose the columns to display in main products and options sections' :
                                'Choose the columns to display, and the order in which they are to appear.'}
                        </BodySmall>
                    </Box>

                    <Box height={'400px'} width={'100%'}>
                        <AgGridTable
                            ref={tableRef}
                            getRowId={getRowId}
                            isLoading={false}
                            showToolbarPanel
                            toolBarPanelOptions={['search']}
                            rowData={rowDataOptions}
                            defaultColDef={defaultColDef}
                            columnDefs={columnDefs}
                            suppressContextMenu={true}
                            suppressMenuHide={true}
                            onTextSearch={handleSearch}
                            onSelectionChanged={handleCheckboxChange}
                            quickFilterText={searchText}
                            gridOptions={{ rowDragManaged: true, rowSelection: 'multiple', suppressRowClickSelection: true, suppressDragLeaveHidesColumns: true }}
                            onGridReady={onGridReady}
                            searchBarFullWidth={true}
                        />
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};
